import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { CSSProperties, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import ButtonBase from '@sorare/core/src/atoms/buttons/ButtonBase';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import Scrollable from '@sorare/core/src/atoms/navigation/Scrollable';
import { LabelL, LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import Dialog from '@sorare/core/src/components/dialog';
import { PodiumIcon } from '@sorare/core/src/components/rivals/Seasons/PodiumIcon';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { BoxOdds } from '@sorare/gameplay/src/components/rewards/BoxOdds';

import { DropdownDivisionPicker } from 'components/rivals/seasons/divisionPicker/DropdownDivisionPicker';
import { ScrollableDivisionPicker } from 'components/rivals/seasons/divisionPicker/ScrollableDivisionPicker';
import { findLeaderboardConfigByDivisionId } from 'lib/rivals';

import { BoxOddsDialog_footballRivalsDivisionLeaderboardConfig } from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 2 })`
  padding: 0 var(--double-unit) var(--double-unit);
`;
const BoxPreview = styled.img`
  width: calc(8 * var(--unit));
  height: calc(8 * var(--unit));
  object-fit: contain;

  &.selected {
    filter: drop-shadow(0 0 20px var(--neon-color));
  }
`;
const RankPreview = styled(Vertical)`
  text-align: center;
  padding: var(--double-unit) 0;
`;
const TitleWrapper = styled(SBHorizontal)`
  padding-right: var(--intermediate-unit);
`;

type Props = {
  leaderboardsConfig: BoxOddsDialog_footballRivalsDivisionLeaderboardConfig[];
  initiallySelectedDivision: BoxOddsDialog_footballRivalsDivisionLeaderboardConfig['divisionConfig'];
  initiallySelectedRank: number;
  onClose: () => void;
  open: boolean;
};
export const BoxOddsDialog = ({
  leaderboardsConfig,
  initiallySelectedDivision,
  initiallySelectedRank,
  open,
  onClose,
}: Props) => {
  const [selectedDivision, setSelectedDivision] = useState(
    initiallySelectedDivision
  );
  const [selectedRank, setSelectedRank] = useState(initiallySelectedRank);
  const isTabletAndAbove = useIsTabletAndAbove();

  const currentDivisionRewardConfig = findLeaderboardConfigByDivisionId(
    leaderboardsConfig,
    selectedDivision.id
  )?.rankingRewardConfig;

  const box = currentDivisionRewardConfig?.find(
    ({ rank }) => rank === selectedRank
  )?.probabilisticBundleConfig;

  if (!box) {
    return null;
  }

  const divisionConfigs = leaderboardsConfig.map(
    ({ divisionConfig }) => divisionConfig
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      title={
        <TitleWrapper>
          <Vertical gap={0}>
            <LabelL bold>
              <FormattedMessage {...fantasy.prizePool} />
            </LabelL>
            {!isTabletAndAbove && <LabelS>{selectedDivision.title}</LabelS>}
          </Vertical>
          {isTabletAndAbove && (
            <DropdownDivisionPicker
              divisionConfigs={divisionConfigs}
              selectedDivision={selectedDivision}
              onSelectDivision={setSelectedDivision}
            />
          )}
        </TitleWrapper>
      }
    >
      <Root>
        {!isTabletAndAbove && (
          <ScrollableDivisionPicker
            divisionConfigs={divisionConfigs}
            selectedDivision={selectedDivision}
            onClick={setSelectedDivision}
          />
        )}
        <Vertical gap={4}>
          <Scrollable itemToDisplay={3}>
            {currentDivisionRewardConfig.map(
              ({ rank, probabilisticBundleConfig }) => (
                <RankPreview key={rank} center gap={2}>
                  <PodiumIcon rank={rank} />
                  <ButtonBase onClick={() => setSelectedRank(rank)}>
                    <BoxPreview
                      src={probabilisticBundleConfig?.config.pictureUrl}
                      alt={probabilisticBundleConfig?.config.name}
                      className={classNames({
                        selected: selectedRank === rank,
                      })}
                      style={
                        {
                          '--neon-color':
                            probabilisticBundleConfig?.config.neonColor ||
                            'var(--c-rivals-arena)',
                        } as CSSProperties
                      }
                    />
                  </ButtonBase>
                  <LabelM bold as="span">
                    {probabilisticBundleConfig?.config.name}
                  </LabelM>
                </RankPreview>
              )
            )}
          </Scrollable>
          <BoxOdds boxConfig={box.config} />
        </Vertical>
      </Root>
    </Dialog>
  );
};

BoxOddsDialog.fragments = {
  footballRivalsDivisionLeaderboardConfig: gql`
    fragment BoxOddsDialog_footballRivalsDivisionLeaderboardConfig on FootballRivalsDivisionLeaderboardConfig {
      rankingRewardConfig {
        rank
        probabilisticBundleConfig {
          id
          config {
            id
            pictureUrl
            name
            neonColor
            ...BoxOdds_probabilisticBundleConfig
          }
        }
      }
      divisionConfig {
        id
        title
        ...ScrollableDivisionPicker_footballRivalsDivisionLeaderboardDivisionConfig
        ...DropdownDivisionPicker_footballRivalsDivisionLeaderboardDivisionConfig
      }
      ...findLeaderboardConfigByDivisionId_leaderboardsConfig
    }
    ${ScrollableDivisionPicker.fragments
      .footballRivalsDivisionLeaderboardDivisionConfig}
    ${DropdownDivisionPicker.fragments
      .footballRivalsDivisionLeaderboardDivisionConfig}
    ${BoxOdds.fragments.probabilisticBundleConfig}
    ${findLeaderboardConfigByDivisionId.fragments
      .footballRivalsDivisionLeaderboardConfig}
  ` as TypedDocumentNode<BoxOddsDialog_footballRivalsDivisionLeaderboardConfig>,
};
